/**
 * BuchhaltungBerichte — SuSa (Saldenliste), GuV ve Bilanz.
 * Tümü konten-METADATA üzerinden üretilir (statement_type,
 * guv_position, balance_position, normal_side) — hesap numarası
 * koda gömülü değildir. Dönem seçilebilir.
 */
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart3, Scale, ListOrdered, Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  listAccounts, fetchAccountTotals, accountBalance, buildGuV, buildBilanz,
  computeUStVA, saveUStVA, generateElsterXml, fmtEUR,
} from '@/lib/buchhaltungApi';

const year = new Date().getFullYear();

const MONTH_NAMES = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

/**
 * UStVA paneli — dönem seçilir, steuer hesapları tax_codes metadata'sından
 * toplanır, Zahllast hesaplanır ve filing olarak kaydedilebilir.
 */
function UStVAPanel() {
  const queryClient = useQueryClient();
  const now = new Date();
  const [ustvaYear, setUstvaYear] = useState(String(now.getFullYear()));
  const [ustvaMonth, setUstvaMonth] = useState(String(now.getMonth() + 1));

  const { data: result, isLoading } = useQuery({
    queryKey: ['fibu-ustva', ustvaYear, ustvaMonth],
    queryFn: () => computeUStVA({ year: Number(ustvaYear), month: Number(ustvaMonth) }),
  });

  const saveMutation = useMutation({
    mutationFn: () => saveUStVA(result),
    onSuccess: () => {
      queryClient.invalidateQueries(['fibu-ustva-filings']);
      toast.success(`UStVA ${ustvaMonth}/${ustvaYear} gespeichert (Status: generiert).`);
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Umsatzsteuer-Voranmeldung</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Select value={ustvaMonth} onValueChange={setUstvaMonth}>
            <SelectTrigger className="h-11 flex-1 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MONTH_NAMES.map((m, i) => (
                <SelectItem key={i} value={String(i + 1)} className="text-xs">{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ustvaYear} onValueChange={setUstvaYear}>
            <SelectTrigger className="h-11 w-28 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[year, year - 1].map((y) => (
                <SelectItem key={y} value={String(y)} className="text-xs">{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading && <p className="py-6 text-center text-sm text-slate-400">Berechne…</p>}
        {result && (
          <>
            <div className="space-y-1.5">
              {result.rows.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                  <span className="text-xs text-slate-700">
                    <Badge variant="outline" className="text-[9px] mr-1.5 font-mono">Kz {r.field || '—'}</Badge>
                    {r.label} ({r.kind})
                  </span>
                  <span className={cn('font-mono text-xs font-bold', r.kind === 'VSt' ? 'text-emerald-700' : 'text-slate-900')}>
                    {r.kind === 'VSt' ? '−' : ''}{fmtEUR(r.amount)}
                  </span>
                </div>
              ))}
              {result.rows.length === 0 && (
                <p className="py-6 text-center text-sm text-slate-400">Keine Steuerbewegungen im Zeitraum.</p>
              )}
            </div>
            <div className="flex items-center justify-between pt-3 border-t-2 border-slate-900">
              <span className="text-sm font-black text-slate-900">
                {result.zahllast >= 0 ? 'Zahllast' : 'Erstattung'}
              </span>
              <span className={cn('font-mono text-sm font-black', result.zahllast >= 0 ? 'text-red-600' : 'text-emerald-700')}>
                {fmtEUR(Math.abs(result.zahllast))}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                className="h-11 bg-slate-900 hover:bg-slate-800 text-xs font-bold"
                disabled={saveMutation.isPending || result.rows.length === 0}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}
              </Button>
              <Button
                variant="outline"
                className="h-11 text-xs font-bold"
                disabled={result.rows.length === 0}
                onClick={async () => {
                  try {
                    await saveUStVA(result);
                    await generateElsterXml(result);
                    toast.success('ELSTER-XML erzeugt und heruntergeladen.');
                  } catch (err) {
                    toast.error('Fehler: ' + err.message);
                  }
                }}
              >
                ELSTER-XML
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 text-center">
              Beträge aus Steuerkonten (tax_codes-Metadaten). XML zur Übergabe an
              Steuerberater/ELSTER-Portal — Direktübermittlung erfordert Zertifikat.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function BuchhaltungBerichte() {
  const [activeTab, setActiveTab] = useState('susa');
  const [dateFrom, setDateFrom] = useState(`${year}-01-01`);
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));

  const { data: accounts = [] } = useQuery({ queryKey: ['fibu-accounts'], queryFn: () => listAccounts() });
  const { data: totals } = useQuery({
    queryKey: ['fibu-totals', dateFrom, dateTo],
    queryFn: () => fetchAccountTotals({ dateFrom, dateTo }),
  });

  const susa = useMemo(() => {
    if (!totals) return [];
    return accounts
      .map((a) => {
        const t = totals.get(a.account_no) || { debit: 0, credit: 0 };
        return { ...a, periodDebit: t.debit, periodCredit: t.credit, balance: accountBalance(a, totals) };
      })
      .filter((a) => a.periodDebit !== 0 || a.periodCredit !== 0);
  }, [accounts, totals]);

  const guv = useMemo(
    () => (totals ? buildGuV(accounts, totals) : { groups: [], revenue: 0, expense: 0, result: 0 }),
    [accounts, totals]
  );
  const bilanz = useMemo(
    () => (totals ? buildBilanz(accounts, totals, guv.result) : { aktiva: [], passiva: [], sumAktiva: 0, sumPassiva: 0 }),
    [accounts, totals, guv.result]
  );

  const TABS = [
    { key: 'susa', label: 'Saldenliste', icon: ListOrdered },
    { key: 'guv', label: 'GuV', icon: BarChart3 },
    { key: 'bilanz', label: 'Bilanz', icon: Scale },
    { key: 'ustva', label: 'UStVA', icon: Receipt },
  ];

  return (
    <PageShell
      head={(
        <div className="flex items-center gap-2">
          <PageHeader icon={<BarChart3 className="w-5 h-5" />} title="Fibu-Berichte" />
        </div>
      )}
    >

      {/* Dönem seçimi */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Von</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-11 bg-white" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Bis</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-11 bg-white" />
        </div>
      </div>

      {/* Sekmeler */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={cn(
              'flex-shrink-0 min-h-[44px] px-4 py-2 text-sm font-semibold border-b-2 -mb-[1px] transition-colors flex items-center gap-1.5 cursor-pointer',
              activeTab === t.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
            )}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* SuSa */}
      {activeTab === 'susa' && (
        <Card className="divide-y divide-slate-100 overflow-hidden">
          <div className="hidden sm:grid grid-cols-[56px_1fr_100px_100px_110px] gap-2 px-3 py-2 bg-slate-900 text-white text-[10px] font-bold uppercase">
            <span>Konto</span><span>Bezeichnung</span>
            <span className="text-right">Soll</span><span className="text-right">Haben</span>
            <span className="text-right">Saldo</span>
          </div>
          {susa.map((a) => (
            <div key={a.account_no} className="px-3 py-2.5 sm:grid sm:grid-cols-[56px_1fr_100px_100px_110px] sm:gap-2 sm:items-center">
              <span className="font-mono text-xs font-bold text-slate-500">{a.account_no}</span>
              <span className="text-xs font-semibold text-slate-800 truncate block">{a.name}</span>
              <span className="hidden sm:block text-right font-mono text-xs text-slate-500">{fmtEUR(a.periodDebit)}</span>
              <span className="hidden sm:block text-right font-mono text-xs text-slate-500">{fmtEUR(a.periodCredit)}</span>
              <div className="flex sm:block items-center justify-between mt-1 sm:mt-0">
                <span className="sm:hidden text-[10px] text-slate-400">
                  S {fmtEUR(a.periodDebit)} · H {fmtEUR(a.periodCredit)}
                </span>
                <span className={cn('text-right font-mono text-xs font-bold block', a.balance < 0 ? 'text-red-600' : 'text-slate-900')}>
                  {fmtEUR(a.balance)}
                </span>
              </div>
            </div>
          ))}
          {susa.length === 0 && <p className="p-8 text-center text-sm text-slate-400">Keine Bewegungen im Zeitraum.</p>}
        </Card>
      )}

      {/* GuV */}
      {activeTab === 'guv' && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Gewinn- und Verlustrechnung</CardTitle></CardHeader>
          <CardContent className="space-y-1.5">
            {guv.groups.map((g) => (
              <div key={g.position} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                <span className="text-xs text-slate-700">{g.position}</span>
                <span className={cn('font-mono text-xs font-bold', g.amount < 0 ? 'text-red-600' : 'text-emerald-700')}>
                  {fmtEUR(g.amount)}
                </span>
              </div>
            ))}
            {guv.groups.length === 0 && <p className="py-6 text-center text-sm text-slate-400">Keine GuV-Bewegungen im Zeitraum.</p>}
            <div className="flex items-center justify-between pt-3 mt-2 border-t-2 border-slate-900">
              <span className="text-sm font-black text-slate-900">Ergebnis</span>
              <span className={cn('font-mono text-sm font-black', guv.result < 0 ? 'text-red-600' : 'text-emerald-700')}>
                {fmtEUR(guv.result)}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* UStVA */}
      {activeTab === 'ustva' && <UStVAPanel />}

      {/* Bilanz */}
      {activeTab === 'bilanz' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { title: 'Aktiva', rows: bilanz.aktiva, sum: bilanz.sumAktiva },
            { title: 'Passiva', rows: bilanz.passiva, sum: bilanz.sumPassiva },
          ].map((side) => (
            <Card key={side.title}>
              <CardHeader className="pb-2"><CardTitle className="text-sm">{side.title}</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {side.rows.map((g) => (
                  <div key={g.position} className="flex items-center justify-between py-1.5 border-b border-slate-50">
                    <span className="text-xs text-slate-700">{g.position}</span>
                    <span className="font-mono text-xs font-bold text-slate-900">{fmtEUR(g.amount)}</span>
                  </div>
                ))}
                {side.rows.length === 0 && <p className="py-4 text-center text-xs text-slate-400">Keine Positionen.</p>}
                <div className="flex items-center justify-between pt-3 mt-2 border-t-2 border-slate-900">
                  <span className="text-sm font-black">Summe {side.title}</span>
                  <span className="font-mono text-sm font-black">{fmtEUR(side.sum)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
          {Math.abs(bilanz.sumAktiva - bilanz.sumPassiva) > 0.01 && (
            <Badge className="md:col-span-2 justify-center bg-amber-100 text-amber-800 border-amber-200 py-2">
              Hinweis: Aktiva ≠ Passiva (Differenz {fmtEUR(bilanz.sumAktiva - bilanz.sumPassiva)}) — offene Abschlussbuchungen prüfen.
            </Badge>
          )}
        </div>
      )}
    </PageShell>
  );
}
